import { featureNavigationAtom } from '@/atoms/featureNavigation.atom';
import { useAtom } from 'jotai';
import { useCallback, useMemo } from 'react';

export interface UseFeatureNavigationReturn {
  canNavigatePrev: boolean;
  canNavigateNext: boolean;
  currentIndex: number;
  totalCount: number;
  navigatePrev: () => void;
  navigateNext: () => void;
  hasNavigation: boolean;
}

export const useFeatureNavigation = (): UseFeatureNavigationReturn => {
  const [navigationContext, setNavigationContext] = useAtom(featureNavigationAtom);

  // Calculate navigation state
  const currentIndex = navigationContext?.currentIndex ?? -1;
  const totalCount = navigationContext?.featureIds.length ?? 0;
  const hasNavigation = navigationContext !== null && totalCount > 1;

  // Calculate boundary conditions
  const canNavigatePrev = hasNavigation && currentIndex > 0;
  const canNavigateNext = hasNavigation && currentIndex < totalCount - 1;

  // Navigate to previous feature
  const navigatePrev = useCallback(() => {
    if (!navigationContext || !canNavigatePrev) return;

    const prevIndex = currentIndex - 1;

    // Update the navigation context with new index
    // The GeoJson component will watch for this change and update the selected feature
    setNavigationContext({
      ...navigationContext,
      currentIndex: prevIndex,
    });
  }, [navigationContext, canNavigatePrev, currentIndex, setNavigationContext]);

  // Navigate to next feature
  const navigateNext = useCallback(() => {
    if (!navigationContext || !canNavigateNext) return;

    const nextIndex = currentIndex + 1;

    // Update the navigation context with new index
    // The GeoJson component will watch for this change and update the selected feature
    setNavigationContext({
      ...navigationContext,
      currentIndex: nextIndex,
    });
  }, [navigationContext, canNavigateNext, currentIndex, setNavigationContext]);

  return useMemo(
    () => ({
      canNavigatePrev,
      canNavigateNext,
      currentIndex,
      totalCount,
      navigatePrev,
      navigateNext,
      hasNavigation,
    }),
    [canNavigatePrev, canNavigateNext, currentIndex, totalCount, navigatePrev, navigateNext, hasNavigation],
  );
};
